'use client';
import { useEffect, useRef, useState } from 'react';

import cn from '@/lib/clsxm';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import Container from '@/components/@base/container';
import ShowMoreButton from '@/components/app/product/components/@shared/section/show-more-button';
import Props from '@/components/app/product/components/@shared/section/type';

import { DATA_CY_SHOW_MORE } from '@/constant/cypress';
import localization from '@/constant/localization';

export default function Section({
  expandable = false,
  onShowMoreClick,
  expandOnShowMoreClick = true,
  showMoreLabel = localization.seeMore,
  children,
  className,
  forcedMaxHeight = 0,
  id,
  ...props
}: Props) {
  const isSmallScreen = useClientSmallScreen();
  const [showMore, setShowMore] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expandable && contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      const maxHeight = forcedMaxHeight > 0 ? forcedMaxHeight : isSmallScreen ? 286 : 400; // max-h value for md screen or default

      setShouldShowButton(contentHeight > maxHeight);
    }
  }, [expandable, children]);

  return (
    <Container
      {...props}
      id={id}
      className={cn(
        'relative m-4 rounded-lg bg-[#FFFFFF] px-3.5 py-[15px] shadow-[0px_10px_25px_0px_#969BA41A] md:mx-0 md:my-5 md:px-[30px] md:py-9',
        expandable && 'max-h-[286px] overflow-hidden md:max-h-[400px]',
        expandable && forcedMaxHeight && `max-h-[${forcedMaxHeight}px]`,
        showMore && 'max-h-none!',
        className,
      )}
      style={
        forcedMaxHeight
          ? {
              maxHeight: `${forcedMaxHeight}px`,
            }
          : undefined
      }
      ref={contentRef}
    >
      {children}

      {expandable && shouldShowButton && !showMore && (
        <ShowMoreButton
          data-cy={DATA_CY_SHOW_MORE}
          label={showMoreLabel}
          onShowMoreClick={() => {
            expandOnShowMoreClick && setShowMore(true);
            onShowMoreClick && onShowMoreClick();
          }}
        />
      )}
    </Container>
  );
}
